import { baseApi } from "@/shared/api";
import type {
  GetNotificationsParams,
  MarkAllReadResponse,
  Notification,
  NotificationPreferences,
  NotificationSettings,
  NotificationsResponse,
  UnreadCountResponse,
  UpdateNotificationPreferencesDto,
  UpdateNotificationSettingsDto,
} from "../types";

interface MarkReadArgs {
  id: number;
  wasUnread: boolean;
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUnreadCount: builder.query<UnreadCountResponse, void>({
      query: () => ({
        url: "/api/notifications/unread-count/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "Notifications", id: "COUNT" }],
    }),
    getNotifications: builder.query<NotificationsResponse, GetNotificationsParams>({
      query: (params) => ({
        url: "/api/notifications/",
        method: "GET",
        credentials: "include" as const,
        params,
      }),
      providesTags: [{ type: "Notifications", id: "LIST" }],
    }),
    markRead: builder.mutation<Notification, MarkReadArgs>({
      query: ({ id }) => ({
        url: `/api/notifications/${id}/read/`,
        method: "POST",
        credentials: "include" as const,
      }),
      async onQueryStarted({ wasUnread }, { dispatch, queryFulfilled }) {
        const patchCount = wasUnread
          ? dispatch(
            notificationsApi.util.updateQueryData("getUnreadCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          )
          : null;
        try {
          await queryFulfilled;
        } catch {
          patchCount?.undo();
        }
      },
      invalidatesTags: [{ type: "Notifications", id: "LIST" }, { type: "Notifications", id: "COUNT" }],
    }),
    markAllRead: builder.mutation<MarkAllReadResponse, void>({
      query: () => ({
        url: "/api/notifications/mark-all-read/",
        method: "POST",
        credentials: "include" as const,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const patchCount = dispatch(
          notificationsApi.util.updateQueryData("getUnreadCount", undefined, (draft) => {
            draft.count = 0;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchCount.undo();
        }
      },
      invalidatesTags: [{ type: "Notifications", id: "LIST" }, { type: "Notifications", id: "COUNT" }],
    }),
    getSettings: builder.query<NotificationSettings, void>({
      query: () => ({
        url: "/api/notifications/settings/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "Notifications", id: "SETTINGS" }],
    }),
    updateSettings: builder.mutation<NotificationSettings, UpdateNotificationSettingsDto>({
      query: (body) => ({
        url: "/api/notifications/settings/",
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "Notifications", id: "SETTINGS" }],
    }),
    getPreferences: builder.query<NotificationPreferences, void>({
      query: () => ({
        url: "/api/notifications/preferences/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "Notifications", id: "PREFERENCES" }],
    }),
    updatePreferences: builder.mutation<NotificationPreferences, UpdateNotificationPreferencesDto>({
      query: (body) => ({
        url: "/api/notifications/preferences/",
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "Notifications", id: "PREFERENCES" }],
    }),
  }),
});

export const {
  useGetUnreadCountQuery,
  useGetNotificationsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
} = notificationsApi;
